<script>
import axios from "@/utils/axios";

export default {
  inject: ["setLoading"],
  data() {
    return {
      title: "Notifications",
      text: "Pages",
      text1: "Notifications",
      data: [],
      filteredData: [],
      pagination: {
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
        has_more: false,
        links: [],
      },
      counts: { total: 0, unread: 0, read: 0 },
      filters: { selectedCategory: "", selectedStatus: "all" },
      loading: false,
    };
  },

  computed: {
    filteredNotifications() {
      let filtered = [...this.data];
      if (this.filters.selectedCategory) {
        filtered = filtered.filter(
          (n) => n.category === this.filters.selectedCategory,
        );
      }
      if (this.filters.selectedStatus === "unread") {
        filtered = filtered.filter((n) => !n.is_read);
      } else if (this.filters.selectedStatus === "read") {
        filtered = filtered.filter((n) => n.is_read);
      }
      return filtered;
    },
    leadCount() {
      return this.data.filter((n) => n.category === "lead").length;
    },
    remainingCount() {
      return Math.max(0, this.pagination.total - this.data.length);
    },
    hasMorePages() {
      return (
        this.pagination.current_page < this.pagination.last_page ||
        this.pagination.has_more
      );
    },
  },

  mounted() {
    this.notifications();
  },

  methods: {
    async loadMore() {
      if (!this.hasMorePages || this.loading) return;
      const nextPage = this.pagination.current_page + 1;
      await this.notifications(nextPage);
    },

    async notifications(page = 1) {
      this.loading = true;
      try {
        const params = {
          page,
          ...(this.filters.selectedCategory && {
            category: this.filters.selectedCategory,
          }),
          ...(this.filters.selectedStatus !== "all" && {
            status: this.filters.selectedStatus,
          }),
        };
        const response = await axios.get("/notifications", { params });

        if (page === 1) {
          this.data = response.data.data.notifications;
        } else {
          this.data = [...this.data, ...response.data.data.notifications];
        }

        this.pagination = response.data.data.pagination;
        this.counts = response.data.data.counts;

        console.log(
          `Page ${page}: ${this.data.length} total items, ${this.remainingCount} remaining`,
        );
      } catch (error) {
        console.error("Notifications error:", error.message);
      } finally {
        this.loading = false;
      }
    },

    filterChanged() {
      this.notifications(1);
    },

    async markAsRead(notificationId) {
      try {
        await this.$markNotificationAsRead(notificationId);
        const notif = this.data.find((n) => n.id === notificationId);
        if (notif) {
          notif.is_read = true;
          this.counts.unread = Math.max(0, this.counts.unread - 1);
        }
      } catch (e) {
        console.error("Mark as read failed:", e);
      }
    },

    async markAllRead() {
      try {
        await this.$markAllNotificationsAsRead();
        this.data.forEach((n) => (n.is_read = true));
        this.counts.unread = 0;
      } catch (e) {
        console.error("Mark all read failed:", e);
      }
    },

    async deleteNotification(notificationId) {
      try {
        await this.$deleteNotification(notificationId);
        this.data = this.data.filter((n) => n.id !== notificationId);
        this.counts.total = Math.max(0, this.counts.total - 1);
      } catch (e) {
        console.error("Delete failed:", e);
      }
    },

    async handleNotificationClick(notification) {
      if (!notification.is_read) {
        await this.markAsRead(notification.id);
      }
      if (notification.action_url) {
        const cleanUrl = notification.action_url.replace(/^\/+/, "/");
        this.$router.push(cleanUrl);
      }
    },

    decodeIcon(icon) {
      return icon
        ? icon.replace(/\\u([\d\w]{4})/gi, (match, grp) =>
            String.fromCharCode(parseInt(grp, 16)),
          )
        : "🔔";
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="`${text1} (${counts.unread || 0} unread)`" />
        <div class="d-flex gap-2">
          <button @click="markAllRead" :disabled="!(counts.unread > 0)" class="btn btn-primary btn-sm d-flex align-items-center gap-1">
            <i class="ti ti-checks"></i> Mark All Read
            <span class="badge bg-white text-primary rounded-pill ms-1" v-if="counts.unread > 0">{{ counts.unread }}</span>
          </button>
        </div>
      </div>

      <!-- Stats Row -->
      <div class="row g-3 mb-3">
        <div class="col-md-4">
          <div class="ntf-stat-card">
            <div class="ntf-stat-icon" style="background:#eef2ff;">
              <i class="ti ti-bell" style="color:#4f46e5;"></i>
            </div>
            <div>
              <span class="ntf-stat-label">Total</span>
              <span class="ntf-stat-value">{{ counts.total || 0 }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="ntf-stat-card">
            <div class="ntf-stat-icon" style="background:#fef3c7;">
              <i class="ti ti-bell-ringing" style="color:#f59e0b;"></i>
            </div>
            <div>
              <span class="ntf-stat-label">Unread</span>
              <span class="ntf-stat-value">{{ counts.unread || 0 }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="ntf-stat-card">
            <div class="ntf-stat-icon" style="background:#dcfce7;">
              <i class="ti ti-bell-check" style="color:#16a34a;"></i>
            </div>
            <div>
              <span class="ntf-stat-label">Read</span>
              <span class="ntf-stat-value">{{ counts.read || 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="card ntf-card">

        <!-- Filters -->
        <div class="ntf-filters">
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <div class="ntf-filter-group">
              <i class="ti ti-category-2 ntf-filter-icon"></i>
              <select v-model="filters.selectedCategory" @change="filterChanged" class="ntf-filter-select">
                <option value="">All Categories</option>
                <option value="lead">Lead Alerts ({{ leadCount }})</option>
              </select>
            </div>
            <div class="ntf-filter-group">
              <i class="ti ti-filter ntf-filter-icon"></i>
              <select v-model="filters.selectedStatus" @change="filterChanged" class="ntf-filter-select">
                <option value="all">All Status</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
              </select>
            </div>
            <button class="ntf-refresh-btn" @click="notifications(1)">
              <i class="ti ti-refresh"></i>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading && data.length === 0" class="ntf-empty-state">
          <div class="ntf-spinner"></div>
          <p>Loading notifications...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && filteredNotifications.length === 0" class="ntf-empty-state">
          <div class="ntf-empty-icon">
            <i class="ti ti-bell-off"></i>
          </div>
          <h5>No notifications found</h5>
          <p>Try adjusting your filters or refresh the page</p>
        </div>

        <!-- Notification List -->
        <div v-else class="ntf-list">
          <div
            v-for="notification in filteredNotifications"
            :key="notification.id"
            class="ntf-item"
            :class="{ 'ntf-unread': !notification.is_read }"
            @click="handleNotificationClick(notification)"
          >
            <!-- Left: Icon -->
            <div class="ntf-item-icon" :style="{ background: (notification.category_color || '#4f46e5') + '18' }">
              <span :style="{ color: notification.category_color || '#4f46e5' }" v-html="decodeIcon(notification.priority_icon)"></span>
            </div>

            <!-- Center: Content -->
            <div class="ntf-item-content">
              <div class="ntf-item-header">
                <h6 class="ntf-item-title">{{ notification.title }}</h6>
                <span v-if="!notification.is_read" class="ntf-new-badge">New</span>
              </div>
              <p class="ntf-item-message">{{ notification.message }}</p>
              <div class="ntf-item-meta">
                <span class="ntf-item-category" :style="{ background: (notification.category_color || '#4f46e5') + '15', color: notification.category_color || '#4f46e5' }">
                  {{ notification.category.toUpperCase() }}
                </span>
                <span class="ntf-item-time">
                  <i class="ti ti-clock"></i> {{ notification.created_at_human }}
                </span>
                <button
                  v-if="notification.action_url"
                  @click.stop="handleNotificationClick(notification)"
                  class="ntf-action-link"
                >
                  {{ notification.action_text || 'View Details' }} <i class="ti ti-arrow-right"></i>
                </button>
              </div>
            </div>

            <!-- Right: Actions -->
            <div class="ntf-item-actions">
              <button
                v-if="!notification.is_read"
                @click.stop="markAsRead(notification.id)"
                class="ntf-btn-read"
                title="Mark as read"
              >
                <i class="ti ti-check"></i>
              </button>
              <button
                @click.stop="deleteNotification(notification.id)"
                class="ntf-btn-delete"
                title="Delete"
              >
                <i class="ti ti-x"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Footer / Load More -->
        <div class="ntf-footer">
          <div v-if="loading && data.length > 0" class="ntf-footer-loading">
            <div class="ntf-spinner-sm"></div>
            <span>Loading more...</span>
          </div>

          <button
            v-else-if="hasMorePages"
            @click="loadMore"
            class="ntf-load-more"
            :disabled="loading"
          >
            <i class="ti ti-arrow-down"></i> Load More
            <span class="ntf-remaining">({{ remainingCount }} remaining)</span>
          </button>

          <div v-else-if="data.length > 0" class="ntf-footer-done">
            <i class="ti ti-circle-check"></i>
            All {{ pagination.total }} notifications loaded
          </div>
        </div>

      </div>
    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
/* ===== Stat Cards ===== */
.ntf-stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #f0f1f5;
  border-radius: 12px;
  padding: 16px 20px;
}

.ntf-stat-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.ntf-stat-label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.ntf-stat-value {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #1a1d23;
  line-height: 1.2;
}

/* ===== Card ===== */
.ntf-card {
  border: none;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}

/* ===== Filters ===== */
.ntf-filters {
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafbfc;
}

.ntf-filter-group {
  position: relative;
  display: flex;
  align-items: center;
}

.ntf-filter-icon {
  position: absolute;
  left: 12px;
  font-size: 14px;
  color: #9ca3af;
  pointer-events: none;
  z-index: 1;
}

.ntf-filter-select {
  padding: 8px 14px 8px 34px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  background: #fff;
  min-width: 170px;
  cursor: pointer;
  transition: border-color 0.15s;
}

.ntf-filter-select:focus {
  outline: none;
  border-color: #4f46e5;
}

.ntf-refresh-btn {
  width: 36px;
  height: 36px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 15px;
  transition: all 0.15s;
}

.ntf-refresh-btn:hover {
  background: #f3f4f6;
  color: #1a1d23;
}

/* ===== Notification List ===== */
.ntf-list {
  padding: 8px 0;
}

.ntf-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px;
  cursor: pointer;
  transition: background 0.15s;
  border-left: 3px solid transparent;
}

.ntf-item:not(:last-child) {
  border-bottom: 1px solid #f7f8f9;
}

.ntf-item:hover {
  background: #f9fafb;
}

.ntf-item.ntf-unread {
  background: #f8faff;
  border-left-color: #4f46e5;
}

.ntf-item.ntf-unread:hover {
  background: #f0f4ff;
}

/* ===== Item Icon ===== */
.ntf-item-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

/* ===== Item Content ===== */
.ntf-item-content {
  flex: 1;
  min-width: 0;
}

.ntf-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.ntf-item-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1d23;
  margin: 0;
  line-height: 1.3;
}

.ntf-new-badge {
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #ef4444;
  padding: 2px 7px;
  border-radius: 10px;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.ntf-item-message {
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ntf-item-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.ntf-item-category {
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.4px;
}

.ntf-item-time {
  font-size: 11.5px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ntf-item-time i {
  font-size: 13px;
}

.ntf-action-link {
  border: none;
  background: none;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 3px;
  transition: color 0.15s;
}

.ntf-action-link:hover {
  color: #3730a3;
}

/* ===== Item Actions ===== */
.ntf-item-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.ntf-btn-read,
.ntf-btn-delete {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.15s;
}

.ntf-btn-read {
  background: #dcfce7;
  color: #16a34a;
}

.ntf-btn-read:hover {
  background: #16a34a;
  color: #fff;
}

.ntf-btn-delete {
  background: #fee2e2;
  color: #dc2626;
}

.ntf-btn-delete:hover {
  background: #dc2626;
  color: #fff;
}

/* ===== Empty / Loading ===== */
.ntf-empty-state {
  padding: 60px 20px;
  text-align: center;
}

.ntf-empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.ntf-empty-icon i {
  font-size: 28px;
  color: #9ca3af;
}

.ntf-empty-state h5 {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 6px;
}

.ntf-empty-state p {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
}

.ntf-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: ntf-spin 0.7s linear infinite;
  margin: 0 auto 14px;
}

.ntf-spinner-sm {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: ntf-spin 0.7s linear infinite;
}

@keyframes ntf-spin {
  to { transform: rotate(360deg); }
}

/* ===== Footer ===== */
.ntf-footer {
  padding: 16px 20px;
  border-top: 1px solid #f3f4f6;
  text-align: center;
}

.ntf-footer-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #9ca3af;
  font-size: 13px;
}

.ntf-load-more {
  border: 1.5px solid #e5e7eb;
  background: #fff;
  color: #374151;
  padding: 10px 28px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}

.ntf-load-more:hover {
  background: #f9fafb;
  border-color: #4f46e5;
  color: #4f46e5;
}

.ntf-remaining {
  font-weight: 400;
  color: #9ca3af;
  font-size: 12px;
}

.ntf-footer-done {
  font-size: 13px;
  font-weight: 500;
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.ntf-footer-done i {
  font-size: 16px;
}
</style>